import type { GroupChannel } from "@sendbird/chat/groupChannel";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useMemo } from "react";
import {
  useGetMyMentorships,
  type MentorshipDetails,
} from "@/lib/api/mentorships";
import {
  devOverridesEnabled,
  useDevOverridesStore,
  type D7TriggerQaScenario,
} from "@/lib/hooks/admin/use-dev-overrides";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import {
  useDismissedKindsByMentorship,
  type DismissedKindsByMentorship,
} from "./action/context/use-action-suggestion-dismissals";
import { toNoticeConversations } from "./notice/notice-conversation-adapter";
import { getNoticeFilterCounts } from "./notice/notice-conversations";
import { buildMentorshipSignalsByUrl } from "./notice/notice-mentorship-signals";

const TEEN_CUSTOM_TYPES = ["MENTORSHIP"];

export interface DashboardSummary {
  teens: number;
  unread: number;
  checkIns: number;
  isLoading: boolean;
}

export function buildDashboardSummary({
  channels,
  currentUserId,
  mentorships,
  isLoading,
  qaScenario = null,
  dismissedByMentorship,
}: {
  channels: GroupChannel[];
  currentUserId: string;
  mentorships: MentorshipDetails[];
  isLoading: boolean;
  qaScenario?: D7TriggerQaScenario | null;
  dismissedByMentorship?: DismissedKindsByMentorship;
}): DashboardSummary {
  const conversations = toNoticeConversations(
    channels,
    currentUserId,
    buildMentorshipSignalsByUrl(mentorships, {
      qaScenario,
      dismissedByMentorship,
    }),
  );
  const counts = getNoticeFilterCounts(conversations);

  return {
    teens: channels.length,
    unread: counts.unread,
    checkIns: counts.checkIn,
    isLoading,
  };
}

export function useDashboardSummary(): DashboardSummary {
  const store = useSendbirdStateContext();
  const currentUserId = store.config.userId;
  const rawQaScenario = useDevOverridesStore(
    (state) => state.d7TriggerQaScenario,
  );
  const qaScenario = devOverridesEnabled ? rawQaScenario : null;
  const dismissedByMentorship = useDismissedKindsByMentorship();

  const { channels, loading: channelsLoading } = useChannelsList({
    customTypesFilter: TEEN_CUSTOM_TYPES,
    enableNotifications: false,
  });
  const { data: mentorships, isLoading: mentorshipsLoading } =
    useGetMyMentorships();

  return useMemo(
    () =>
      buildDashboardSummary({
        channels,
        currentUserId,
        mentorships: mentorships ?? [],
        isLoading: channelsLoading || mentorshipsLoading,
        qaScenario,
        dismissedByMentorship,
      }),
    [
      channels,
      currentUserId,
      mentorships,
      channelsLoading,
      mentorshipsLoading,
      qaScenario,
      dismissedByMentorship,
    ],
  );
}
